import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/** Paged list envelope every document list endpoint returns. */
interface Paged<T = any> {
  data: T[];
  total: number;
}

export interface DashboardSnapshot {
  /** Purchase orders */
  activePos: Paged | null;
  draftPos: number | null;
  expiredPosWithoutGrn: number | null;
  /** Goods receipts */
  grns: Paged | null;
  draftGrns: number | null;
  /** Transfers */
  outboundInTransit: number | null;
  inboundInTransit: number | null;
  draftTransfers: number | null;
  /** Purchase returns */
  prns: number | null;
  /** Stock */
  lowStock: any[] | null;
  stockSummary: any | null;
  /** Stock takes */
  stockTakesInProgress: number | null;
  /** Customer returns */
  returnsInbox: { PENDING_APPROVAL: number; REQUIRES_RECONCILIATION: number } | null;
}

/**
 * Operations overview data. The backend has no aggregate KPI endpoint, so the
 * dashboard reads the same paged list endpoints the modules use (limit=1 for
 * counts, limit=5 for recent rows). Every call is independent: a module the
 * user cannot access (403) or that fails simply reports null and its tile is
 * hidden, so one broken module never blanks the whole page.
 */
@Injectable({ providedIn: 'root' })
export class DashboardService {
  private http = inject(HttpClient);
  private api = environment.apiUrl;

  load(locationId: string | null): Observable<DashboardSnapshot> {
    const paged = (path: string, params: Record<string, string | number>) =>
      this.http
        .get<Paged>(`${this.api}/${path}`, { params: new HttpParams({ fromObject: params as any }) })
        .pipe(catchError(() => of(null)));
    const count = (path: string, params: Record<string, string | number>) =>
      paged(path, { ...params, page: 1, limit: 1 }).pipe(map((r) => (r ? Number(r.total ?? 0) : null)));

    return forkJoin({
      activePos: paged('purchase-order', { page: 1, limit: 5, isExpired: 'false' }),
      draftPos: count('purchase-order', { status: 'DRAFT' }),
      expiredPosWithoutGrn: count('purchase-order', { expiredWithoutGrn: 'true' }),
      grns: paged('grn', { page: 1, limit: 5 }),
      draftGrns: count('grn', { status: 'DRAFT' }),
      outboundInTransit: count('stock-transfer-out', { direction: 'outgoing', status: 'IN_TRANSIT' }),
      inboundInTransit: count('stock-transfer-out', { direction: 'incoming', status: 'IN_TRANSIT' }),
      draftTransfers: count('stock-transfer-out', { status: 'DRAFT' }),
      prns: count('prn', {}),
      lowStock: locationId
        ? this.http
            .get<any[]>(`${this.api}/inventory/low-stock`, { params: { locationId } })
            .pipe(catchError(() => of(null)))
        : of(null),
      stockSummary: locationId
        ? this.http
            .get<any>(`${this.api}/inventory/location-summary/${locationId}`)
            .pipe(catchError(() => of(null)))
        : of(null),
      stockTakesInProgress: count('stock-take/session', { status: 'IN_PROGRESS' }),
      returnsInbox: this.http
        .get<{ PENDING_APPROVAL: number; REQUIRES_RECONCILIATION: number }>(
          `${this.api}/returns/inbox-counts`,
        )
        .pipe(catchError(() => of(null))),
    });
  }
}
